import React from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import "./App.css";
import { Toaster } from "./components/ui/toaster";
import { ThemeProvider } from "./context/ThemeContext";
import ThemeToggle from "./components/ThemeToggle";
import LanguageToggle from "./components/LanguageToggle";
import Header from "./components/Header";
import Hero from "./components/Hero";
import ProductShowcase from "./components/ProductShowcase";
import NeonCustomizer from "./components/NeonCustomizer";
import Gallery from "./components/Gallery";
import Features from "./components/Features";
import Testimonials from "./components/Testimonials";
import QuoteForm from "./components/QuoteForm";
import Footer from "./components/Footer";
import ProductCategory from "./components/ProductCategory";
import LogoDesign from "./components/LogoDesign";
import BusinessEnhancement from "./components/BusinessEnhancement";
import AdminLogin from "./components/AdminLogin";
import AdminDashboard from "./components/AdminDashboard";
import FloatingFormWidget from "./components/FloatingFormWidget";
import SocialWidget from "./components/SocialWidget";
import SignRentals from "./components/SignRentals";
import QuotePortal from "./components/QuotePortal";
import CommercialDisclosure from "./pages/CommercialDisclosure";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import BusinessStudio from "./pages/BusinessStudio";
import TrackingScripts from "./components/TrackingScripts";

// Home page component with all sections
const HomePage = () => {
  return (
    <>
      <Hero />
      <ProductShowcase />
      <div id="customizer">
        <NeonCustomizer />
      </div>
      <div id="gallery">
        <Gallery />
      </div>
      <div id="features">
        <Features />
      </div>
      <Testimonials />
      <div id="quote">
        <QuoteForm />
      </div>
    </>
  );
};

// Layout wrapper that conditionally shows header/footer
const MainLayout = ({ children }) => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isQuotePortal = location.pathname.startsWith('/quote/');
  
  return (
    <div className="App min-h-screen transition-colors duration-300">
      {!isAdminRoute && !isQuotePortal && <Header />}
      {!isAdminRoute && !isQuotePortal && <ThemeToggle />}
      {!isAdminRoute && !isQuotePortal && <LanguageToggle />}
      {!isAdminRoute && !isQuotePortal && <FloatingFormWidget />}
      {!isAdminRoute && !isQuotePortal && <SocialWidget />}
      {children}
      {!isAdminRoute && !isQuotePortal && <Footer />}
      <Toaster />
    </div>
  );
};

// Simple admin wrapper - completely bypasses React Router for auth
const AdminWrapper = () => {
  const path = window.location.pathname;
  const hash = window.location.hash;
  
  // Handle Google OAuth callback
  if (hash?.includes('session_id=')) {
    const sessionIdMatch = hash.match(/session_id=([^&]+)/);
    if (sessionIdMatch) {
      const sessionId = sessionIdMatch[1];
      // Process auth inline - use same origin
      const API_URL = window.location.origin;
      
      fetch(`${API_URL}/api/auth/google/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ session_id: sessionId })
      })
      .then(res => res.json())
      .then(data => {
        if (data.access_token && data.user) {
          localStorage.setItem('adminToken', String(data.access_token));
          localStorage.setItem('adminUser', JSON.stringify({
            user_id: String(data.user.user_id || ''),
            username: String(data.user.username || ''),
            email: String(data.user.email || ''),
            name: String(data.user.name || ''),
            picture: String(data.user.picture || ''),
            role: String(data.user.role || 'admin')
          }));
          window.location.replace('/admin/dashboard');
        } else {
          window.location.replace('/admin/login');
        }
      })
      .catch(() => {
        window.location.replace('/admin/login');
      });
      
      return (
        <div className="min-h-screen flex items-center justify-center bg-black">
          <div className="text-center">
            <div className="animate-spin w-12 h-12 border-4 border-pink-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-white text-lg">Completing authentication...</p>
          </div>
        </div>
      );
    }
  }
  
  if (path === '/admin/login' || path === '/admin') {
    return <AdminLogin />;
  }
  
  if (path === '/admin/dashboard') {
    return <AdminDashboard />;
  }
  
  return null;
};

function App() {
  // Check if we're on an admin route - handle outside React Router
  const isAdminPath = window.location.pathname.startsWith('/admin');
  const hasSessionId = window.location.hash?.includes('session_id=');
  
  if (isAdminPath || hasSessionId) {
    return (
      <ThemeProvider>
        <div className="App min-h-screen transition-colors duration-300">
          <TrackingScripts />
          <AdminWrapper />
          <Toaster />
        </div>
      </ThemeProvider>
    );
  }
  
  return (
    <ThemeProvider>
      <Router>
        <TrackingScripts />
        <MainLayout>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/products/:slug" element={<ProductCategory />} />
            <Route path="/services/logo-design" element={<LogoDesign />} />
            <Route path="/services/business-enhancement" element={<BusinessEnhancement />} />
            <Route path="/services/sign-rentals" element={<SignRentals />} />
            <Route path="/services/business-studio" element={<BusinessStudio />} />
            <Route path="/business-studio" element={<BusinessStudio />} />
            <Route path="/quote/:accessToken" element={<QuotePortal />} />
            <Route path="/commercial-disclosure" element={<CommercialDisclosure />} />
            <Route path="/tokushoho" element={<CommercialDisclosure />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
          </Routes>
        </MainLayout>
      </Router>
    </ThemeProvider>
  );
}

export default App;
